import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import { Megaphone, Users, RefreshCw, X, Check } from 'lucide-react';
import { useMobile } from '../hooks/useMobile';

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' };
}

function fmtDate(iso) {
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── Select Introducers modal ────────────────────────────────────────────────
function PickerModal({ introducers, selected, setSelected, onClose, onSend, sending }) {
  const allIds = introducers.map((i) => i.id);
  const allSelected = allIds.length > 0 && selected.length === allIds.length;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Select Introducers</h2>
          <button style={styles.iconBtn} onClick={onClose}><X size={16} /></button>
        </div>
        <div
          style={styles.selectAllRow}
          onClick={() => setSelected(allSelected ? [] : allIds)}
        >
          <span style={{ ...styles.checkbox, ...(allSelected ? styles.checkboxOn : {}) }}>
            {allSelected && <Check size={12} color="#fff" />}
          </span>
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            {allSelected ? 'Clear all' : 'Select all'} ({allIds.length})
          </span>
        </div>
        <div style={styles.modalList}>
          {introducers.length === 0 ? (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: '24px 0' }}>No active introducers yet.</p>
          ) : (
            introducers.map((i) => {
              const checked = selected.includes(i.id);
              return (
                <div
                  key={i.id}
                  style={styles.pickerRow}
                  onClick={() => setSelected(checked ? selected.filter((x) => x !== i.id) : [...selected, i.id])}
                >
                  <span style={{ ...styles.checkbox, ...(checked ? styles.checkboxOn : {}) }}>
                    {checked && <Check size={12} color="#fff" />}
                  </span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{i.name}</div>
                    {i.orgName ? <div style={{ fontSize: 12, color: '#9ca3af' }}>{i.orgName}</div> : null}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <button
          style={{ ...styles.sendBtn, ...(selected.length === 0 ? styles.btnDisabled : {}) }}
          disabled={selected.length === 0 || sending}
          onClick={onSend}
        >
          {sending ? 'Sending…' : `Send to ${selected.length} selected`}
        </button>
      </div>
    </div>
  );
}

export default function BrokerAnnouncementsPage() {
  const mobile = useMobile();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const [introducers, setIntroducers] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [selected, setSelected] = useState([]);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadIntroducers = () => {
    fetch('/api/introducers', { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        const orgs = d.introducers || d || [];
        // Flatten org.members (individual introducer USERS, not the org row)
        // — same pattern already used for the manual-lead-entry picker in
        // LeadsPage.jsx. Never send introducers.id (the org row) as a target.
        const flat = orgs.flatMap((org) =>
          (org.members || []).map((m) => ({ id: m.id, name: m.name, orgName: org.name }))
        );
        setIntroducers(flat);
      })
      .catch(() => {});
  };

  const loadHistory = () => {
    setLoadingHistory(true);
    fetch('/api/broker/announcements', { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setHistory(d.announcements || []))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  };

  useEffect(() => { loadIntroducers(); loadHistory(); }, []);

  const validate = () => {
    if (!title.trim() || !body.trim()) {
      setError('Title and message are required');
      return false;
    }
    setError('');
    return true;
  };

  const send = async (introducerUserIds) => {
    if (!validate()) return;
    setSending(true);
    setError('');
    try {
      const res = await fetch('/api/broker/announcements', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ title: title.trim(), body: body.trim(), introducerUserIds: introducerUserIds || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send announcement');
      alert(`Announcement sent to ${data.announcement.recipient_count} introducer${data.announcement.recipient_count === 1 ? '' : 's'}.`);
      setTitle('');
      setBody('');
      setSelected([]);
      setShowPicker(false);
      loadHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleSendAll = () => {
    if (!validate()) return;
    if (!window.confirm(`Send this to all ${introducers.length} active introducer${introducers.length === 1 ? '' : 's'}?`)) return;
    send(null);
  };

  const content = (
    <>
      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: '#0e1b2c', margin: '0 0 4px' }}>Announcements</h1>
          <p style={{ color: '#6b7280', margin: 0, fontSize: 14 }}>Broadcast an update to your introducers</p>
        </div>
        <button onClick={loadHistory} style={styles.iconBtn}><RefreshCw size={15} /></button>
      </div>

      {/* Composer */}
      <div style={styles.card}>
        <label style={styles.label}>Title</label>
        <input
          style={styles.input}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. New product launched"
        />
        <label style={{ ...styles.label, marginTop: 14 }}>Message</label>
        <textarea
          style={styles.textarea}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Short update for your introducers…"
          rows={4}
        />
        {error ? <p style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{error}</p> : null}

        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <button style={styles.primaryBtn} onClick={handleSendAll} disabled={sending}>
            <Megaphone size={16} /> Send to All ({introducers.length})
          </button>
          <button style={styles.secondaryBtn} onClick={() => { if (validate()) setShowPicker(true); }} disabled={sending}>
            <Users size={16} /> Select Introducers
          </button>
        </div>
      </div>

      {/* History */}
      <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0e1b2c', margin: '28px 0 12px' }}>Sent History</h2>
      {loadingHistory ? (
        <p style={{ color: '#9ca3af' }}>Loading…</p>
      ) : history.length === 0 ? (
        <div style={styles.empty}>No announcements sent yet.</div>
      ) : (
        <div style={styles.table}>
          {history.map((h) => (
            <div key={h.id} style={styles.historyRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#0e1b2c' }}>{h.title}</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.body}</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{fmtDate(h.created_at)} · {h.audience === 'all' ? 'All introducers' : 'Selected introducers'}</div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 13, color: '#374151', whiteSpace: 'nowrap' }}>
                {h.recipient_count} sent<br />
                <span style={{ color: '#9ca3af' }}>{h.read_count} read</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: mobile ? 0 : '70px', padding: mobile ? '64px 16px 24px' : '24px', minHeight: '100vh', background: '#f9fafb' }}>
        <div style={{ maxWidth: 900 }}>{content}</div>
      </div>
      {showPicker && (
        <PickerModal
          introducers={introducers}
          selected={selected}
          setSelected={setSelected}
          onClose={() => setShowPicker(false)}
          onSend={() => send(selected)}
          sending={sending}
        />
      )}
    </>
  );
}

const styles = {
  card: { background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', padding: 20 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' },
  primaryBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  secondaryBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: '#eff6ff', color: '#10b981', border: '1px solid #10b981', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  iconBtn: { display: 'flex', alignItems: 'center', padding: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', color: '#6b7280' },
  empty: { padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 14, background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb' },
  table: { background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' },
  historyRow: { display: 'flex', justifyContent: 'space-between', gap: 16, padding: '14px 16px', borderBottom: '1px solid #f3f4f6' },

  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { background: '#fff', borderRadius: 12, padding: 20, width: 420, maxWidth: '90vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  selectAllRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' },
  modalList: { overflowY: 'auto', flex: 1, margin: '4px 0 16px' },
  pickerRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', cursor: 'pointer', borderBottom: '1px solid #f9fafb' },
  checkbox: { width: 18, height: 18, borderRadius: 4, border: '1.5px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  checkboxOn: { background: '#10b981', borderColor: '#10b981' },
  sendBtn: { width: '100%', padding: '12px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
};
